// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import type { Bundle, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { QuestionnaireForm } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { getIntakeMedplumClient } from '../../lib/bot-client';
import { botIdentifier } from '../../lib/bot-identifier';
import { stripNoneOfTheAboveIfSiblings } from '../../lib/exclusion-cleanup';
import SleepHistoryContent from './SleepHistoryContent';

type Step = 'loading' | 'medical-history' | 'sleep-history' | 'error';

interface DeferredIntakeQuestionnaireModalProps {
  opened: boolean;
  onClose: () => void;
  uniqueId?: string;
}

export function DeferredIntakeQuestionnaireModal({
  opened,
  onClose,
  uniqueId,
}: DeferredIntakeQuestionnaireModalProps): JSX.Element {
  const [step, setStep] = useState<Step>('loading');
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [submitting, setSubmitting] = useState(false);
  const [_error, setError] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setStep('loading');
    setError(false);
    Promise.all([
      fetch('/public-questionnaires.json').then((response) => response.json() as Promise<Bundle>),
      getIntakeMedplumClient(),
    ])
      .then(async ([bundle, client]) => {
        const questionnaire = (bundle.entry ?? [])
          .map((entry) => entry.resource)
          .find(
            (resource): resource is Questionnaire =>
              resource?.resourceType === 'Questionnaire' && resource.name === 'eligibility-check-page-1'
          );
        if (!questionnaire) {
          throw new Error('Intake questionnaire not found');
        }
        setQuestionnaire(questionnaire);
        if (!uniqueId) {
          throw new Error('Patient onboarding identifier missing');
        }
        const status = (await client.executeBot(
          botIdentifier('onboarding-status'),
          { uniqueId },
          'application/json'
        )) as { phase?: string };
        if (status.phase === 'eligibility-appointment-booked') {
          await client.executeBot(
            botIdentifier('process-eligibility-step'),
            { uniqueId, phase: 'eligibility-appointment-booked', sendWelcomeEmail: true },
            'application/json'
          );
        } else if (status.phase === 'sleep-history') {
          // The medical history is already saved; pick up at the Epworth and STOP-BANG.
          setStep('sleep-history');
          return;
        } else if (status.phase !== 'eligibility-check-page-1') {
          throw new Error(`Unexpected intake questionnaire phase: ${status.phase ?? 'unknown'}`);
        }
        setStep('medical-history');
      })
      .catch((err) => {
        Sentry.captureException(err);
        setError(true);
        setStep('error');
      });
  }, [opened, uniqueId]);

  const submitMedicalHistory = async (response: QuestionnaireResponse): Promise<void> => {
    setSubmitting(true);
    try {
      const client = await getIntakeMedplumClient();
      await client.executeBot(
        botIdentifier('process-eligibility-step'),
        {
          uniqueId,
          phase: 'eligibility-check-page-1',
          questionnaireResponse: stripNoneOfTheAboveIfSiblings(
            stripNoneOfTheAboveIfSiblings(response, 'diagnoses'),
            'clinical-conditions'
          ),
        },
        'application/json'
      );
      setStep('sleep-history');
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const submitSleepHistory = async (ess: QuestionnaireResponse, stopBang: QuestionnaireResponse): Promise<void> => {
    setSubmitting(true);
    try {
      const client = await getIntakeMedplumClient();
      await client.executeBot(
        botIdentifier('process-eligibility-step'),
        {
          uniqueId,
          phase: 'sleep-history',
          questionnaireResponseEpworth: ess,
          questionnaireResponseStopBang: stopBang,
        },
        'application/json'
      );
      onClose();
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Complete your intake questionnaire" centered size="lg">
      {step === 'loading' && <Loader />}
      {step === 'error' && <Alert color="red">We could not load your questionnaire. Please try again.</Alert>}
      {step === 'medical-history' && questionnaire && (
        <Stack>
          <Text c="dimmed">Please answer these questions before your intake appointment.</Text>
          <div ref={formRef}>
            <QuestionnaireForm questionnaire={questionnaire} onSubmit={submitMedicalHistory} excludeButtons />
          </div>
          <Group justify="flex-end">
            <Button loading={submitting} onClick={() => formRef.current?.querySelector('form')?.requestSubmit()}>
              Continue
            </Button>
          </Group>
        </Stack>
      )}
      {step === 'sleep-history' && <SleepHistoryContent submitting={submitting} onSubmit={submitSleepHistory} />}
    </Modal>
  );
}
